/**
 * The party when a track is finished: confetti over the whole screen (the
 * track player too) and a short chime, beside the "Track complete" toast.
 * `celebrate` in lib/training.ts calls `celebrateTrack`, so every way a track
 * can finish — a tick, a quiz pass, a video, a process — has it once.
 */
export const TRACK_COMPLETED_EVENT = "carbon-os:track-completed";

/** The person asked for less motion: the toast says it, no confetti. */
export function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

type AudioContextClass = typeof AudioContext;

/**
 * A rising C–E–G–C on a soft triangle wave, about a second long, made here so
 * there is no sound file to ship. Quietly nothing where the browser has no Web
 * Audio, or will not start it.
 */
export function playCheer(): void {
  const Context: AudioContextClass | undefined =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioContextClass }).webkitAudioContext;
  if (!Context) return;
  try {
    const context = new Context();
    const start = context.currentTime + 0.02;
    [523.25, 659.25, 783.99, 1046.5].forEach((frequency, index) => {
      const at = start + index * 0.11;
      const last = index === 3;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = "triangle";
      oscillator.frequency.setValueAtTime(frequency, at);
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.18, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + (last ? 0.7 : 0.25));
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(at);
      oscillator.stop(at + (last ? 0.75 : 0.3));
    });
    window.setTimeout(() => void context.close().catch(() => undefined), 1500);
  } catch {
    // Audio is a nicety: the toast and the confetti still happen.
  }
}

let lastCheer = 0;

/**
 * Starts the party for `trackName`; `Confetti` (mounted once in App) draws it.
 * One step can finish several tracks at once: one chime for all of them.
 */
export function celebrateTrack(trackName: string, now: number = Date.now()): void {
  if (now - lastCheer > 1500) playCheer();
  lastCheer = now;
  window.dispatchEvent(new CustomEvent(TRACK_COMPLETED_EVENT, { detail: { trackName } }));
}
